/**
 * QuickBooks API Service
 * Handles all communication with the backend QuickBooks endpoints
 * Backend now stores connection info in database (realmId, access/refresh tokens, expiry dates)
 */

import { BaseService } from './BaseService';

/**
 * QuickBooks service for OAuth integration
 */
class QuickBooksService extends BaseService {
    constructor() {
        super('/api/quickbooks');
    }

    /**
     * Check QuickBooks connection status
     * Returns connection info from database including token expiry details
     * 
     * @param {string} accessToken - Azure AD access token
     * @returns {Promise<Object>} Connection status:
     * {
     *   authenticated: boolean,
     *   realmId?: string,
     *   accessTokenExpiresAt?: string,      // ISO 8601 datetime
     *   accessTokenExpired?: boolean,
     *   refreshTokenExpiresAt?: string,     // ISO 8601 datetime
     *   refreshTokenExpired?: boolean,
     *   message?: string
     * }
     */
    async getStatus(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/status');
        } catch (error) {
            console.error('Failed to get QuickBooks status:', error);
            throw new QuickBooksApiError(
                'Failed to get QuickBooks status',
                error.status,
                error
            );
        }
    }

    /**
     * Get QuickBooks OAuth authorization URL
     * 
     * @param {string} accessToken - Azure AD access token
     * @returns {Promise<Object>} Authorization URL response:
     * {
     *   authorizationUrl: string,
     *   message: string
     * }
     */
    async getAuthUrl(accessToken) {
        try {
            const response = await this.makeRequest(accessToken, '/auth');
            
            if (!response.authorizationUrl) {
                throw new QuickBooksApiError(
                    'No authorization URL received from backend',
                    null,
                    response
                );
            }
            
            return response;
        } catch (error) {
            console.error('Failed to get QuickBooks authorization URL:', error);
            throw new QuickBooksApiError(
                'Failed to get QuickBooks authorization URL',
                error.status,
                error
            );
        }
    }

    /**
     * Disconnect from QuickBooks (revoke tokens and clear database records)
     * Backend will revoke OAuth tokens and delete connection info from database
     * 
     * @param {string} accessToken - Azure AD access token
     * @returns {Promise<Object>} Disconnect response:
     * {
     *   message: string,
     *   status: string
     * }
     */
    async disconnect(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/disconnect', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
        } catch (error) {
            console.error('Failed to disconnect from QuickBooks:', error);
            throw new QuickBooksApiError(
                'Failed to disconnect from QuickBooks',
                error.status,
                error
            );
        }
    }

    /**
     * Refresh access token (optional - backend auto-refreshes)
     * This is handled automatically by the backend when tokens expire,
     * but can be called manually if needed
     * 
     * @param {string} accessToken - Azure AD access token
     * @returns {Promise<Object>} Refresh response
     */
    async refreshToken(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/refresh', {
                method: 'POST'
            });
        } catch (error) {
            console.error('Failed to refresh QuickBooks token:', error);
            throw new QuickBooksApiError(
                'Failed to refresh QuickBooks token',
                error.status,
                error
            );
        }
    }

    /**
     * Search for QuickBooks customers by name
     * 
     * @param {string} accessToken - Azure AD access token
     * @param {string} searchTerm - Customer name to search for
     * @returns {Promise<Array>} Array of matching customers
     */
    async searchCustomers(accessToken, searchTerm) {
        try {
            const response = await this.makeRequest(
                accessToken, 
                `/customers/search?name=${encodeURIComponent(searchTerm)}`
            );
            return response;
        } catch (error) {
            console.error('Failed to search QuickBooks customers:', error);
            throw new QuickBooksApiError(
                'Failed to search QuickBooks customers',
                error.status,
                error
            );
        }
    }

    /**
     * Get a QuickBooks customer by ID
     * 
     * @param {string} accessToken - Azure AD access token
     * @param {string} customerId - QuickBooks customer ID
     * @returns {Promise<Object>} Customer details:
     * {
     *   customer: {
     *     id: string,
     *     displayName: string,
     *     givenName?: string,
     *     familyName?: string,
     *     companyName?: string,
     *     primaryEmailAddr?: string,
     *     primaryPhone?: string,
     *     mobile?: string,
     *     billAddrLine1?: string,
     *     billAddrCity?: string,
     *     billAddrCountrySubDivisionCode?: string,
     *     billAddrPostalCode?: string,
     *     balance?: number,
     *     active: boolean,
     *     syncToken: string,
     *     metaDataCreateTime?: string,
     *     metaDataLastUpdatedTime?: string
     *   },
     *   message: string
     * }
     */
    async getCustomerById(accessToken, customerId) {
        try {
            const response = await this.makeRequest(
                accessToken, 
                `/customers/${encodeURIComponent(customerId)}`
            );
            return response;
        } catch (error) {
            console.error('Failed to get QuickBooks customer:', error);
            throw new QuickBooksApiError(
                'Failed to get QuickBooks customer',
                error.status,
                error
            );
        }
    }

    /**
     * Create a new QuickBooks customer
     * 
     * @param {string} accessToken - Azure AD access token
     * @param {Object} customerData - Customer data
     * @param {string} customerData.displayName - Required: Customer display name
     * @param {string} [customerData.givenName] - First name
     * @param {string} [customerData.middleName] - Middle name
     * @param {string} [customerData.familyName] - Last name
     * @param {string} [customerData.companyName] - Company name
     * @param {string} [customerData.primaryEmailAddr] - Email address
     * @param {string} [customerData.primaryPhone] - Primary phone
     * @param {string} [customerData.mobile] - Mobile phone
     * @param {string} [customerData.billAddrLine1] - Billing address line 1
     * @param {string} [customerData.billAddrLine2] - Billing address line 2
     * @param {string} [customerData.billAddrCity] - Billing city
     * @param {string} [customerData.billAddrCountrySubDivisionCode] - Billing state/province
     * @param {string} [customerData.billAddrPostalCode] - Billing postal code
     * @param {string} [customerData.billAddrCountry] - Billing country
     * @param {string} [customerData.notes] - Customer notes
     * @returns {Promise<Object>} Created customer response:
     * {
     *   customer: QuickBooksCustomer,
     *   message: string,
     *   customerId: string
     * }
     */
    async createCustomer(accessToken, customerData) {
        try {
            const response = await this.makeRequest(
                accessToken,
                '/customers',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(customerData)
                }
            );
            return response;
        } catch (error) {
            console.error('Failed to create QuickBooks customer:', error);
            throw new QuickBooksApiError(
                'Failed to create QuickBooks customer',
                error.status,
                error
            );
        }
    }

    /**
     * Update an existing QuickBooks customer
     * 
     * @param {string} accessToken - Azure AD access token
     * @param {string} customerId - QuickBooks customer ID
     * @param {Object} customerData - Customer data (must include id and syncToken)
     * @param {string} customerData.id - Required: Customer ID (must match customerId parameter)
     * @param {string} customerData.syncToken - Required: Current syncToken for optimistic locking
     * @param {string} customerData.displayName - Required: Customer display name
     * @param {string} [customerData.givenName] - First name
     * @param {string} [customerData.middleName] - Middle name
     * @param {string} [customerData.familyName] - Last name
     * @param {string} [customerData.companyName] - Company name
     * @param {string} [customerData.primaryEmailAddr] - Email address
     * @param {string} [customerData.primaryPhone] - Primary phone
     * @param {string} [customerData.mobile] - Mobile phone
     * @param {string} [customerData.billAddrLine1] - Billing address line 1
     * @param {string} [customerData.billAddrLine2] - Billing address line 2
     * @param {string} [customerData.billAddrCity] - Billing city
     * @param {string} [customerData.billAddrCountrySubDivisionCode] - Billing state/province
     * @param {string} [customerData.billAddrPostalCode] - Billing postal code
     * @param {string} [customerData.billAddrCountry] - Billing country
     * @param {string} [customerData.notes] - Customer notes
     * @returns {Promise<Object>} Updated customer response:
     * {
     *   customer: QuickBooksCustomer,
     *   message: string
     * }
     */
    async updateCustomer(accessToken, customerId, customerData) {
        try {
            const response = await this.makeRequest(
                accessToken,
                `/customers/${encodeURIComponent(customerId)}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(customerData)
                }
            );
            return response;
        } catch (error) {
            console.error('Failed to update QuickBooks customer:', error);
            throw new QuickBooksApiError(
                'Failed to update QuickBooks customer',
                error.status,
                error
            );
        }
    }
}

/**
 * Custom error class for QuickBooks API errors
 */
export class QuickBooksApiError extends Error {
    constructor(message, status, details) {
        super(message);
        this.name = 'QuickBooksApiError';
        this.status = status;
        this.details = details;
    }
}

// Export singleton instance
const quickbooksService = new QuickBooksService();
export default quickbooksService;
